<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="4">
            <FormulateInput
              id="name"
              v-model="filters.name"
              :label="$t('Nome')"
              :placeholder="$t('Nome')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="storeId"
              v-model="filters.storeId"
              type="vue-select"
              :label="$t('Loja')"
              :placeholder="$t('Todas')"
              :options="storeOptions"
              :instruction="
                $t('Traz também as políticas de escopo Geral, que alcançam todas as lojas')
              "
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="priceTableId"
              v-model="filters.priceTableId"
              type="vue-select"
              :label="$t('Tabela de preço')"
              :placeholder="$t('Todas')"
              :options="getComboPriceTables"
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              id="status"
              v-model="filters.status"
              type="vue-select"
              :label="$t('Status')"
              :placeholder="$t('Todos')"
              :options="activeInactiveTypes()"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        ref="installment-policies-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($t('Política de parcelamento'))"
        :fields="fields"
        :items="policies"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <!--
          O escopo é resumido por contagem, e não pela lista de nomes: uma política pode alcançar
          dezenas de lojas, e listá-las tornaria a grade ilegível. Zero significa "todas".
        -->
        <template #cell(storeScopeCount)="row">
          <span v-if="row.item.storeScopeCount === 0">{{ $t('Todas') }}</span>
          <span v-else>{{ row.item.storeScopeCount }} {{ $tc('loja', row.item.storeScopeCount) }}</span>
        </template>

        <template #cell(priceTableScopeCount)="row">
          <span v-if="row.item.priceTableScopeCount === 0">{{ $t('Todas') }}</span>
          <span v-else>{{ row.item.priceTableScopeCount }}</span>
        </template>

        <template #cell(applySurchargeToCustomer)="row">
          <e-status-badge
            :status="row.item.applySurchargeToCustomer"
            :type="'yesNo'"
          />
        </template>

        <template #cell(acquirerCostConfigName)="row">
          {{ row.item.acquirerCostConfigName }}
          <!--
            A configuração de custo inativa não quebra a política, mas o custo previsto deixa de
            refletir um contrato vigente. Sinalizar é melhor que impedir: a política pode ter sido
            desenhada para um contrato que acabou de ser substituído.
          -->
          <b-badge
            v-if="row.item.hasInactiveCostConfig"
            variant="light-warning"
            class="ml-50"
          >
            {{ $t('inativa') }}
          </b-badge>
        </template>

        <template #cell(active)="row">
          <e-status-badge :status="row.item.active" />
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :show-update="true"
            :show-delete="true"
            @update="updatePolicy(row)"
            @delete="deletePolicyLocal(row.item)"
          />
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="installment-policies-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <fab
      :main-tooltip="$t('Adicionar política de parcelamento')"
      @click="createPolicy"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { BRow, BCol, BPagination, BTable, BBadge } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { EPageSizeSelector, EPaginationSummary } from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { statusTypes } from '@/mixins'
import EStatusBadge from '@/views/components/EStatusBadge.vue'
import EFilters from '@/views/components/EFilters.vue'
import EGridActions from '@/views/components/EGridActions.vue'

export default {
  components: {
    BRow,
    BCol,
    BBadge,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    fab,
    EStatusBadge,
    EFilters,
    EGridActions,
  },

  mixins: [statusTypes],

  data() {
    return {
      fetching: false,
    }
  },

  computed: {
    ...mapState('pages/settings/financial/installmentPolicy', {
      policies: 'policies',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),
    ...mapGetters('app', ['storeOptions']),
    ...mapGetters('common/priceTables', ['getComboPriceTables']),

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Nome'),
          key: 'name',
          class: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('Lojas'),
          key: 'storeScopeCount',
          class: 'text-center',
          thStyle: { width: '110px' },
        },
        {
          label: this.$t('Tabelas'),
          key: 'priceTableScopeCount',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Prioridade'),
          key: 'priority',
          class: 'text-center',
          thStyle: { width: '110px' },
          sortable: true,
        },
        {
          label: this.$t('Máx. parcelas'),
          key: 'maxInstallments',
          class: 'text-center',
          thStyle: { width: '120px' },
        },
        {
          label: this.$t('Repassa acréscimo'),
          key: 'applySurchargeToCustomer',
          class: 'text-center',
          thStyle: { width: '150px' },
        },
        {
          label: this.$t('Configuração de custo'),
          key: 'acquirerCostConfigName',
          class: 'text-left',
        },
        {
          label: this.$t('Status'),
          key: 'active',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
      ]
    },
  },

  mounted() {
    this.getData()
  },

  methods: {
    ...mapActions('pages/settings/financial/installmentPolicy/installmentPolicyMaintain', {
      stCleanStateOfMaintain: 'cleanState',
    }),
    ...mapActions('pages/settings/financial/installmentPolicy', [
      'fetchPolicies',
      'deletePolicy',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchPolicies()
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },

    filterData() {
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    updatePolicy(row) {
      const { id } = row.item
      this.$router.push({ name: 'installment-policy-maintain', params: { id } })
    },

    createPolicy() {
      this.stCleanStateOfMaintain()
      this.$router.push({ name: 'installment-policy-add' })
    },

    /**
     * A API recusa a exclusão de política já aplicada a algum pagamento: apagá-la destruiria a
     * rastreabilidade de vendas já realizadas.
     */
    async deletePolicyLocal(policy) {
      try {
        const confirm = await this.confirm()
        if (!confirm) return

        await this.deletePolicy(policy.id)
        this.getData()
        this.showSuccess({ message: this.$t('Política excluída com sucesso') })
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
